import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { OnboardingData, OnboardingStep, Section } from '../types/onboarding'
import { initialOnboardingData } from '../types/onboarding'
import { isShortVariant, isOrganicV2Variant, getVariant } from '../lib/variant'
import { stepsVersionFor } from '../lib/trackingCore'
import { organicV2Steps } from '../lib/experimentCore'

interface OnboardingStore {
  currentStep: number
  /** Identity of the step list `currentStep` indexes into (plan §A4). */
  stepsVersion: string | null
  data: OnboardingData
  navigationDirection: 'forward' | 'back'

  setCurrentStep: (step: number) => void
  updateData: (data: Partial<OnboardingData>) => void
  goNext: (data?: Partial<OnboardingData>) => void
  /** Jump forward to a step by key (a paid buyer without an account: email → createAccount). */
  goToStep: (key: string, data?: Partial<OnboardingData>) => void
  goBack: () => void
  reset: () => void
  /** Reset the saved step index if the active step list changed. Call after captureVariant(). */
  syncStepsVersion: () => void

  getSteps: () => OnboardingStep[]
  getTotalSteps: () => number
  getProgressPercentage: () => number
  getCurrentSection: () => Section
}

const allSteps: OnboardingStep[] = [
  // Profile section - emotional hooks + medical info
  { key: 'painPoints', section: 'profile' },
  { key: 'solution', section: 'profile' },
  { key: 'socialProof', section: 'profile' },
  { key: 'primaryGoal', section: 'profile' },
  { key: 'kidneyStatus', section: 'profile' },
  { key: 'diagnosisTimeline', section: 'profile' },
  { key: 'comorbidities', section: 'profile' },
  { key: 'weight', section: 'profile' },
  { key: 'dialysis', section: 'profile' },

  // Lifestyle section
  { key: 'dietKnowledge', section: 'lifestyle' },
  { key: 'struggles', section: 'lifestyle' },
  { key: 'eatingStyle', section: 'lifestyle' },
  { key: 'testimonial', section: 'lifestyle' },
  { key: 'motivation', section: 'lifestyle' },
  { key: 'commitment', section: 'lifestyle' },
  { key: 'featurePriority', section: 'lifestyle' },

  // Motivation section - email + analysis
  { key: 'email', section: 'motivation' },
  { key: 'analysis', section: 'motivation' },
  { key: 'profileReveal', section: 'motivation' },
  { key: 'demoScan', section: 'motivation' },

  // Plan section - offer + account
  { key: 'offer', section: 'plan' },
  { key: 'createAccount', section: 'plan' },
  { key: 'success', section: 'plan' },
]

// Short funnel for 'meta-short' variant. Tuned 2026-06-06/07 for cold paid Meta
// traffic based on the friction audit (#6):
//   - DemoScan FIRST — its meta-variant preview phase doubles as the ad-hook
//     screen (bold "Stop guessing what you can eat" copy + clickable meal photo
//     that pays off the ad click immediately, no intermediate page).
//   - UserScan SECOND (their own food — known DTC conversion driver)
//   - Personal data AFTER trust (stage → weight → comorbidities)
//   - Drop PrimaryGoal (redundant once KidneyStatus is known)
// SocialProof, Email, Offer, CreateAccount, Success unchanged at the end.
//
// 2026-06-07 — removed the standalone `hook` step. It was a near-duplicate
// of DemoScan's preview (same photo + similar CTA), forcing visitors to tap
// twice for the same conceptual action. The hook copy now lives inside
// DemoScan's preview on meta variants. See HookStep.tsx — file retained as
// reference but no longer routed to.
//
// Account + success are post-purchase, not counted in funnel-length math.
const shortSteps: OnboardingStep[] = [
  { key: 'demoScan', section: 'motivation' },
  { key: 'userScan', section: 'motivation' },
  { key: 'kidneyStatus', section: 'profile' },
  { key: 'weight', section: 'profile' },
  { key: 'comorbidities', section: 'profile' },
  { key: 'socialProof', section: 'profile' },
  { key: 'email', section: 'motivation' },
  { key: 'offer', section: 'plan' },
  { key: 'createAccount', section: 'plan' },
  { key: 'success', section: 'plan' },
]

// Organic A/B treatment ('organic-v2', docs/FUNNEL-ORGANIC-V2.md): allSteps
// minus the web equivalents of the app's winning `minimal` onboarding cuts
// (diagnosisTimeline, dietKnowledge, struggles, eatingStyle, motivation,
// commitment, featurePriority — see experimentCore.ORGANIC_V2_REMOVED_STEPS).
// Medical steps, testimonial and the conversion tail are identical to control.
const organicV2StepList: OnboardingStep[] = organicV2Steps(allSteps)

function resolveSteps(): OnboardingStep[] {
  if (isShortVariant()) return shortSteps
  if (isOrganicV2Variant()) return organicV2StepList
  return allSteps
}

function currentStepsVersion(): string {
  return stepsVersionFor(getVariant(), resolveSteps().map((s) => s.key))
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      currentStep: 0,
      stepsVersion: null,
      data: initialOnboardingData,
      navigationDirection: 'forward',

      setCurrentStep: (step) => set({ currentStep: step }),

      updateData: (newData) =>
        set((state) => ({
          data: { ...state.data, ...newData },
        })),

      goNext: (newData) => {
        const state = get()
        const steps = state.getSteps()
        const updatedData = newData ? { ...state.data, ...newData } : state.data

        // Skip dialysis step if not on dialysis (only relevant when dialysis is in
        // the active step list — short variant omits it entirely)
        let nextStep = state.currentStep + 1
        const nextStepKey = steps[nextStep]?.key
        if (nextStepKey === 'dialysis' && updatedData.kidneyStatus !== 'on_dialysis') {
          nextStep++
        }

        set({
          currentStep: Math.min(nextStep, steps.length - 1),
          data: updatedData,
          navigationDirection: 'forward',
        })
      },

      goToStep: (key, newData) => {
        const state = get()
        const idx = state.getSteps().findIndex((s) => s.key === key)
        if (idx < 0) return
        set({
          currentStep: idx,
          data: newData ? { ...state.data, ...newData } : state.data,
          navigationDirection: 'forward',
        })
      },

      goBack: () => {
        const state = get()
        const steps = state.getSteps()
        // A paid buyer finishing setup came straight from the email step: back
        // returns there (never to the offer they already bought) and drops the mode.
        if (state.data.paidSetup && steps[state.currentStep]?.key === 'createAccount') {
          const emailIdx = steps.findIndex((s) => s.key === 'email')
          if (emailIdx >= 0) {
            set({ currentStep: emailIdx, data: { ...state.data, paidSetup: false }, navigationDirection: 'back' })
            return
          }
        }
        if (state.currentStep > 0) {
          // Skip dialysis step going back too
          let prevStep = state.currentStep - 1
          const prevStepKey = steps[prevStep]?.key
          if (prevStepKey === 'dialysis' && state.data.kidneyStatus !== 'on_dialysis') {
            prevStep--
          }
          set({
            currentStep: Math.max(prevStep, 0),
            navigationDirection: 'back',
          })
        }
      },

      reset: () =>
        set({
          currentStep: 0,
          data: initialOnboardingData,
          navigationDirection: 'forward',
        }),

      // A persisted index saved against a different step list (a deploy that
      // reorders steps, or a variant switch) would land the user on the wrong
      // screen — start the list over instead. Answers already given are kept.
      //  - Legacy state (saved before stepsVersion existed) adopts the current
      //    version without a reset: the lists did not change in that deploy.
      //  - A buyer who already paid is never sent back to the quiz: they go
      //    to account creation in the new list.
      syncStepsVersion: () => {
        const version = currentStepsVersion()
        const state = get()
        if (state.stepsVersion === version) return
        if (state.stepsVersion === null) {
          set({ stepsVersion: version })
          return
        }
        const steps = resolveSteps()
        const accountIdx = steps.findIndex((s) => s.key === 'createAccount')
        const paid = !!state.data.paddleTransactionId || !!state.data.paidSetup
        set({
          stepsVersion: version,
          currentStep: paid && accountIdx >= 0 ? accountIdx : 0,
          navigationDirection: 'forward',
        })
      },

      getSteps: () => resolveSteps(),

      getTotalSteps: () => resolveSteps().length,

      getProgressPercentage: () => {
        const state = get()
        const steps = resolveSteps()
        return ((state.currentStep + 1) / steps.length) * 100
      },

      getCurrentSection: () => {
        const state = get()
        const steps = resolveSteps()
        return steps[state.currentStep]?.section || 'profile'
      },
    }),
    {
      name: 'kidneypal-onboarding-v1',
      partialize: (state) => ({
        currentStep: state.currentStep,
        stepsVersion: state.stepsVersion,
        data: state.data,
      }),
    }
  )
)
